import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight'
import { faHouseMedical } from '@fortawesome/pro-regular-svg-icons/faHouseMedical'
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot'
import { faLocationDotSlash } from '@fortawesome/pro-regular-svg-icons/faLocationDotSlash'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons/faBadgeCheck'
import { cityStatecodeString } from '@modules/trials/helpers/parseLocationsFromTrial/parseTruncatedLocationsFromTrial'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useState } from 'react'
import { type TrialLocationInMap } from './Trial2ListingMap.client'
import { Trial2SelectedLocationSidebar } from './Trial2SelectedLocationSidebar'

const ROWS_TO_SHOW = 1
const ITEMS_PER_ROW = 4

export const Trial2MapSidebar = ({
  locationsToRender,
  selectedIndex,
  setHoveredIndex,
  setSelectedIndex,
}: {
  locationsToRender: TrialLocationInMap[]
  selectedIndex: number
  setHoveredIndex: (i: number) => void
  setSelectedIndex: (i: number) => void
}) => {
  const [expanded, setExpanded] = useState(false)
  const showExpanded = ROWS_TO_SHOW * ITEMS_PER_ROW < locationsToRender.length
  const selectedLocation = locationsToRender[selectedIndex] ?? null

  if (selectedLocation) {
    return (
      <Trial2SelectedLocationSidebar
        location={selectedLocation}
        unselectLocation={() => setSelectedIndex(-1)}
      />
    )
  }

  return (
    <div className='h-72 w-full overflow-auto bg-white p-6 md:h-[500px] md:w-1/2'>
      <Text styleName='h5' value='Research Locations Nearby' />
      <Text
        className='text-neutral600'
        styleName='p-small'
        value='Select from list below to view details:'
      />
      {(expanded
        ? locationsToRender
        : locationsToRender.slice(0, ROWS_TO_SHOW * ITEMS_PER_ROW)
      ).map((location, index) => (
        <LocationOption
          key={location.id}
          location={location}
          onClick={() => {
            setSelectedIndex(index)
          }}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(-1)}
          selected={index === selectedIndex}
        />
      ))}
      {showExpanded && (
        <div
          className='my-5 cursor-pointer rounded-2xl border-2 border-neutral100 px-10 py-1 text-center'
          onClick={() => {
            setExpanded(!expanded)
          }}
        >
          <Icon
            className='mr-2'
            icon={expanded ? faLocationDotSlash : faLocationDot}
          />
          {expanded ? 'Less' : 'More'} Trial Locations
        </div>
      )}
    </div>
  )
}

const LocationOption = ({
  location,
  onClick,
  onMouseEnter,
  onMouseLeave,
  selected,
}: {
  location: TrialLocationInMap
  onClick: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  selected: boolean
}) => {
  return (
    <div
      className={classMerge(
        'group flex cursor-pointer items-center justify-between rounded-md p-2',
        selected ? 'bg-primary100' : 'hover:bg-neutral100',
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className='flex align-baseline'>
        <Icon
          className='mr-2 mt-2 inline-block text-primary500'
          icon={faHouseMedical}
        />
        <div>
          <span>
            <Text
              className='line-clamp-1 inline'
              styleName='p-small'
              value={
                <>
                  {location.facility}
                  {location.monetization && (
                    <Icon
                      className='ml-2 inline-block text-primary500'
                      icon={faBadgeCheck}
                    />
                  )}
                </>
              }
            />
          </span>
          <Text styleName='p-tiny' value={cityStatecodeString(location)} />
        </div>
      </div>

      <Icon
        className='ml-2 text-white group-hover:text-neutral600'
        icon={faArrowRight}
      />
    </div>
  )
}
